// import components
import { LoadingButton } from '@mui/lab';
import { Button, Chip, Stack, Typography } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers-pro';
import Dialog from '../Dialog';

// import enums
import { ChipSelection } from './enum';

// import hooks
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import queries & mutations
import { useDeleteAbsenceMutation } from './queries';

// import types
import type { Absence, Employee } from 'components/types';

// import utils
import { addDays, isBefore, isSameDay, startOfDay } from 'date-fns';
import { formatByLocale, fromISOString, toLocalISOString } from 'utils/date';
import dayjs from 'dayjs';
import {
  StyledDeleteAbsenceDialogBox,
  StyledDeleteAbsenceDurationBox,
  StyledDeleteAbsenceFooterBox,
  StyledDeleteAbsenceMainBox,
  StyledErrorTypography
} from './DeleteAbsenceDialog.styles';
import { StyledCustomMediumDialogPaper } from '../Paper/StyledPaper.styles';

type Props = {
  absence: Absence;
  clickedDate: Date;
  employee: Employee;
  onClickCancel?: () => void;
  onSaveDone?: () => void;
};

const DeleteAbsenceDialog = (props: Props) => {
  'use memo';

  const { absence, clickedDate, employee } = props;
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [selectedChip, setSelectedChip] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null
  ]);

  const { mutate: deleteAbsence, isLoading } = useDeleteAbsenceMutation();

  const hasValidDateRange = () => {
    if (selectedChip && selectedChip !== ChipSelection.CUSTOM) {
      return true;
    } else if (!(dateRange[0] && dateRange[1])) {
      return false;
    } else if (isSameDay(dateRange[0], dateRange[1])) {
      return true;
    } else {
      return (
        dateRange[0] && dateRange[1] && !isBefore(dateRange[1], dateRange[0])
      );
    }
  };

  const isAbsenceDurationOneDay = isSameDay(
    fromISOString(absence.startTime),
    fromISOString(absence.endTime)
  );

  useEffect(() => {
    if (isAbsenceDurationOneDay) {
      setDateRange([
        fromISOString(absence.startTime),
        fromISOString(absence.endTime)
      ]);
    }
  }, [absence.endTime, absence.startTime, isAbsenceDurationOneDay]);

  const onChipClick = (name) => {
    setSelectedChip(name);

    switch (name) {
      case ChipSelection.ENTIRE_ABSENCE:
        setDateRange([
          fromISOString(absence.startTime),
          fromISOString(absence.endTime)
        ]);
        break;
      default:
        setDateRange([startOfDay(clickedDate), startOfDay(clickedDate)]);
        break;
    }
  };

  const onCancelClick = () => {
    setIsOpen(false);
    props.onClickCancel?.();
  };

  const onDeleteClick = async () => {
    let startDate, endDate;

    if (
      selectedChip === ChipSelection.ENTIRE_ABSENCE ||
      isAbsenceDurationOneDay
    ) {
      startDate = null;
      endDate = null;
    } else if (selectedChip === ChipSelection.SELECTED_DATE) {
      startDate = startOfDay(clickedDate);
      endDate = startOfDay(addDays(clickedDate, 1));
    } else {
      startDate = dateRange[0] != null ? startOfDay(dateRange[0]) : null;
      endDate =
        dateRange[1] != null ? startOfDay(addDays(dateRange[1], 1)) : null;
    }
    await deleteAbsence({
      absenceId: absence.absenceId,
      startDate: startDate ? toLocalISOString(startDate) : null,
      endDate: endDate ? toLocalISOString(endDate) : null
    });

    setIsOpen(false);
    props.onSaveDone?.();
  };

  return (
    <Dialog
      open={isOpen}
      title={t('deleteAbsenceDialog.title')}
      slots={{
        paper: StyledCustomMediumDialogPaper
      }}
    >
      <StyledDeleteAbsenceMainBox>
        <Typography variant="body2">
          {t('deleteAbsenceDialog.youAreAboutToDeleteForEmployee', {
            employeeName: employee.name
          })}{' '}
          {isAbsenceDurationOneDay
            ? t('deleteAbsenceDialog.thisAbsenceIsOn', {
                date: formatByLocale(fromISOString(absence.startTime))
              })
            : t('deleteAbsenceDialog.thisAbsenceIsFromUntil', {
                fromDate: formatByLocale(fromISOString(absence.startTime)),
                toDate: formatByLocale(fromISOString(absence.endTime))
              })}
          {isAbsenceDurationOneDay ? (
            <> {t('deleteAbsenceDialog.areYouSure')}</>
          ) : (
            <> {t('deleteAbsenceDialog.selectPartOfAbsenceBelow')}</>
          )}
        </Typography>

        {!isAbsenceDurationOneDay && (
          <StyledDeleteAbsenceDurationBox>
            <Chip
              variant={
                selectedChip === ChipSelection.ENTIRE_ABSENCE
                  ? 'filled'
                  : 'outlined'
              }
              color={
                selectedChip === ChipSelection.ENTIRE_ABSENCE
                  ? 'primary'
                  : undefined
              }
              onClick={() => onChipClick(ChipSelection.ENTIRE_ABSENCE)}
              label={t('deleteAbsenceDialog.entireAbsence')}
            />
            <Chip
              variant={
                selectedChip === ChipSelection.SELECTED_DATE
                  ? 'filled'
                  : 'outlined'
              }
              color={
                selectedChip === ChipSelection.SELECTED_DATE
                  ? 'primary'
                  : undefined
              }
              onClick={() => onChipClick(ChipSelection.SELECTED_DATE)}
              label={formatByLocale(clickedDate)}
            />
            <Chip
              variant={
                selectedChip === ChipSelection.CUSTOM ? 'filled' : 'outlined'
              }
              color={
                selectedChip === ChipSelection.CUSTOM ? 'primary' : undefined
              }
              onClick={() => onChipClick(ChipSelection.CUSTOM)}
              label={t('custom')}
            />
          </StyledDeleteAbsenceDurationBox>
        )}
      </StyledDeleteAbsenceMainBox>
      {selectedChip === ChipSelection.CUSTOM && (
        <Stack>
          <StyledDeleteAbsenceDialogBox>
            <DesktopDatePicker
              value={dateRange[0]}
              minDate={fromISOString(absence.startTime)}
              maxDate={fromISOString(absence.endTime)}
              onChange={(val) =>
                setDateRange([val ? dayjs(val).toDate() : null, dateRange[1]])
              }
              enableAccessibleFieldDOMStructure={false}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  size: 'small',
                  fullWidth: true
                }
              }}
            />
            <Typography variant="body2">{t('until')}</Typography>
            <DesktopDatePicker
              value={dateRange[1]}
              minDate={fromISOString(absence.startTime)}
              maxDate={fromISOString(absence.endTime)}
              onChange={(val) =>
                setDateRange([dateRange[0], val ? dayjs(val).toDate() : null])
              }
              enableAccessibleFieldDOMStructure={false}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  size: 'small',
                  fullWidth: true
                }
              }}
            />
          </StyledDeleteAbsenceDialogBox>
          {!hasValidDateRange() && (
            <StyledErrorTypography variant="body2">
              {t('dateRangeMismatch', {
                startDate: t('startDate'),
                endDate: t('endDate')
              })}
            </StyledErrorTypography>
          )}
        </Stack>
      )}
      <StyledDeleteAbsenceFooterBox>
        <Button variant="outlined" onClick={onCancelClick}>
          {t('cancel')}
        </Button>
        <LoadingButton
          loading={isLoading}
          variant="contained"
          color="primary"
          disabled={!hasValidDateRange()}
          onClick={onDeleteClick}
        >
          {t('delete')}
        </LoadingButton>
      </StyledDeleteAbsenceFooterBox>
    </Dialog>
  );
};

export default DeleteAbsenceDialog;
